/**
 * One service, opened.
 *
 * The head restates everything the row could only hint at - the icon at a size
 * you can actually read, the full description, the code - and carries the two
 * actions that belong to the service itself. Under it sit that service's
 * packages, rendered by the packages half scoped to this product, so there is
 * one packages list in the app rather than a second one written for this screen.
 */
import { ChevronLeft, Pencil, Trash2 } from "lucide-react";
import { ServiceIcon, localeText, makeT, serviceBadgeKey, serviceKind, type Locale, type ProductDTO } from "@wa/ui";
import { Btn, Card, IconBtn, StatusPill } from "../../../ui";
import { PlansPanel } from "../plans";

export function ServiceDetail({
  product,
  locale,
  onBack,
  onEdit,
  onDelete,
}: {
  product: ProductDTO;
  locale: Locale;
  onBack: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const t = makeT(locale);
  const description = product.description ? localeText(product.description, locale) : "";

  return (
    <div className="space-y-4">
      <div>
        <Btn small onClick={onBack}>
          <ChevronLeft className="size-4" />
          {t("services.back")}
        </Btn>
      </div>

      {/* `.crm-card` is a surface and nothing else - background, corners, shadow -
          so a card holding plain content brings its own padding. */}
      <Card className="px-6 py-5">
        <div className="flex items-start gap-4">
          {/* The same disc every list row uses for a person or a company, with the
              service's glyph in place of initials, so the object reads as the same
              kind of thing on both screens. */}
          <span className="crm-av crm-av-soft" style={{ width: 52, height: 52 }} aria-hidden>
            <ServiceIcon name={product.icon} className="size-6" />
          </span>

          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2.5">
              <h2 className="crm-card-t crm-trunc">{localeText(product.name, locale)}</h2>
              <StatusPill tone={serviceKind(product) === "display" ? "mut" : "info"}>
                {t(serviceBadgeKey(serviceKind(product)))}
              </StatusPill>
            </div>
            <span className="crm-prow-s block">{product.code}</span>
            {description ? <p className="crm-sub mt-2.5 max-w-[62ch]">{description}</p> : null}
          </div>

          <div className="crm-push flex flex-none gap-2">
            <Btn small onClick={onEdit}>
              <Pencil className="size-3.5" />
              {t("services.editService")}
            </Btn>
            {/* Missing rather than disabled on the platform service: the API
                protects it outright, and a control that is present but never
                works is a worse explanation than no control. */}
            {product.isPlatform ? null : (
              <IconBtn
                small
                onClick={onDelete}
                title={t("services.deleteService")}
                aria-label={t("services.deleteService")}
              >
                <Trash2 className="size-4" />
              </IconBtn>
            )}
          </div>
        </div>
      </Card>

      <div className="space-y-3">
        <h3 className="crm-card-t px-1.5">{t("services.packages")}</h3>
        <PlansPanel lockedProductCode={product.code} />
      </div>
    </div>
  );
}
